import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Badge, Button } from "nlib/ui";
import {
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getSelectedBusinessClasses,
  getSelectedBusinessData,
  getSelectedBusinessId,
  getSelectedBusinessLocations,
  getSelectedBusinessProjects,
  getSelectedBusinessTaxRates
} from "selectors/businesses";
import { getActiveOrganization, getAllUsersData } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AccountNames from "const/AccountNames";
import Actions from "./lib/Actions";
import AdditionalInfo from "./lib/AdditionalInfo";
import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import Arrays from "utils/Arrays";
import Attachment from "nlib/common/TransactionsTable/lib/Attachment";
import Comments from "./lib/Comments";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import Item from "./lib/Item";
import LocalStorageKeys from "const/LocalStorageKeys";
import OrganizationsActions from "actions/OrganizationsActions";
import React, { useCallback, useMemo } from "react";
import ReasonInput from "nlib/common/ReasonInput";
import SelectCategory from "./lib/SelectCategory";
import SelectClass from "./lib/SelectClass";
import SelectContact from "./lib/SelectContact";
import SelectLocation from "./lib/SelectLocation";
import SelectProject from "./lib/SelectProject";
import SelectTaxRate from "./lib/SelectTaxRate";
import Storage from "utils/Storage";
import TransactionTypes from "const/TransactionTypes";
import Transactions from "utils/Transactions";
import UiActions from "actions/UiActions";
import classNames from "classnames";
import moment from "moment";
import useEnvVars from "hooks/useEnvVars";

const ALWAYS_VISIBLE_INPUTS_COUNT = 1;

const ExtraCell = (props) => {
  const {
    disabled,
    inProgress,
    auditRoute,
    simplifyLayout = false,
    selected,
    selectable,
    transaction,
    transactionState,
    readyToProcess,
    readyToApprove,
    localReasons,
    refetchTableData,
    onChange,
    onReasonBlur,
    onSelectedChange
  } = props;

  const [, setEnvVars] = useEnvVars();

  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const { countryCode } = useSelector(getActiveOrganization);

  const taxRates = useSelector(getSelectedBusinessTaxRates);

  const classes = useSelector(getSelectedBusinessClasses);

  const locations = useSelector(getSelectedBusinessLocations);

  const projects = useSelector(getSelectedBusinessProjects);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const usersData = useSelector(getAllUsersData);

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const {
    settings: { advancedDocumentsWorkflow } = {}
  } = useSelector(getSelectedBusinessData);

  const {
    id: transactionId,
    description,
    status,
    type,
    currency,
    reason: initialReason,
    category: initialCategory,
    usesItems,
    extraData,
    comments,
    documentId,
    reconciled = false,
    askClientInfo,
    aiProcessing,
    attachmentId,
    attachmentName,
    attachmentKey
  } = transaction;

  const {
    vendorId,
    reason,
    manualMode = false,
    address = {},
    category = {},
    item = {},
    taxRate = {},
    class: classValue = {},
    location = {},
    project = {}
  } = transactionState;

  const advancedType = useMemo(() => Transactions.getTransactionAdvancedType({ type, extraData }), [type, extraData]);

  const billPayment = advancedType === AdvancedTransactionTypes.BILL_PAYMENT;

  const receivePayment = advancedType === AdvancedTransactionTypes.RECEIVED_PAYMENT;

  const typeTransfer = advancedType === AdvancedTransactionTypes.TRANSFER;

  const usCountry = countryCode === Countries.US;

  const needReactionStatus = status === DataStatuses.NEED_REACTION;

  const seeTheDocument = !auditRoute && !!documentId && advancedDocumentsWorkflow;

  const showVendorValidation = !auditRoute && (status !== DataStatuses.EXPORTED) && (status !== DataStatuses.TO_REPORT);

  const disableInputs = disabled
    || inProgress
    || aiProcessing
    || (status === DataStatuses.EXPORTED && !quickBooksBusiness)
    || status === DataStatuses.TO_REPORT
    || (!!auditRoute && xeroBusiness);

  const disabledExtraInput = typeTransfer || seeTheDocument || (
    auditRoute ? (xeroBusiness || billPayment || receivePayment) : disableInputs
  );

  const disabledCategoryInput = billPayment || receivePayment || disableInputs || seeTheDocument;

  const handleContactChange = useCallback((value) => {
    onChange(transactionId, { address: value, vendorId: value.id });
  }, [transactionId, onChange]);

  const handleReasonChange = useCallback((value) => {
    onChange(transactionId, { reason: value });
  }, [transactionId, onChange]);

  const handleItemChange = useCallback((value) => {
    onChange(transactionId, {
      item: value,
      tags: value.id ? [value.name] : [],
      class: Arrays.findById(classes, value.classId, classValue)
    });
  }, [onChange, transactionId, classes, classValue]);

  const updateTransaction = useCallback((update) => {
    onChange(transactionId, update);
  }, [transactionId, onChange]);

  const handleCategoryChange = useCallback((value) => {
    updateTransaction({ category: value, tags: value.code ? [value.name] : [] });
  }, [updateTransaction]);

  const handleTaxRateChange = useCallback((value) => {
    updateTransaction({ taxRate: value });
  }, [updateTransaction]);

  const handleClassChange = useCallback((newValue) => {
    updateTransaction({ class: newValue });
  }, [updateTransaction]);

  const handleLocationChange = useCallback((newValue) => {
    updateTransaction({ location: newValue });
  }, [updateTransaction]);

  const handleProjectChange = useCallback((newValue) => {
    updateTransaction({ project: newValue });
  }, [updateTransaction]);

  const handleAskClient = useCallback(async() => {
    if (askClientInfo?.askedEmail) {
      const businessUser = usersData.find(({ email }) => email === askClientInfo?.askedEmail);

      const savedEmailLsKey = `${LocalStorageKeys.LAST_ASKED_BUSINESS_EMAIL}_${selectedBusinessId}`;

      const successful = await dispatch(
        OrganizationsActions.askBusinessUser([transactionId], askClientInfo?.askedEmail, businessUser?.phone)
      );

      if (successful) {
        Storage.storageValue(savedEmailLsKey, `${askClientInfo?.askedEmail}:${moment().unix()}`);
        refetchTableData();
      }
    } else if (!selected) {
      onSelectedChange(transactionId, true);
    }
  }, [
    askClientInfo?.askedEmail,
    selected,
    usersData,
    selectedBusinessId,
    dispatch,
    transactionId,
    onSelectedChange,
    refetchTableData
  ]);

  const handleManualModeSet = useCallback(() => {
    updateTransaction({ manualMode: !manualMode });
  }, [updateTransaction, manualMode]);

  const handleViewCategorizationDetailsClick = useCallback(() => {
    setEnvVars({ transactionId });
  }, [transactionId, setEnvVars]);

  const handleDocumentEdit = useCallback(async() => {
    const result = await dispatch(UiActions.showEditDocumentWindow({ documentId }));

    if (result) refetchTableData();
  }, [dispatch, documentId, refetchTableData]);

  const additionalInputsCount = useMemo(() => {
    if (simplifyLayout) return ALWAYS_VISIBLE_INPUTS_COUNT;

    return [
      !usCountry && !!taxRates.length, !!classes.length, !!locations.length, !!projects.length
    ].filter(Boolean).length + ALWAYS_VISIBLE_INPUTS_COUNT;
  }, [classes.length, locations.length, projects.length, simplifyLayout, taxRates.length, usCountry]);

  return (
    <div className={Css.extraCell}>
      <div className={Css.upper}>
        <div className={Css.description}>
          <div className={Css.content}>
            <div className={classNames(Css.text, !description && Css.muted)} title={description}>
              {description ? <Icons.Info /> : <Icons.Warning className={Css.warning} />}
              <span>
                {description ? description : `${uiTexts.noBankDescriptionOrMemo} :(`}
              </span>
            </div>
          </div>
        </div>
        {rpaMode && (
          <>
            {!auditRoute && !inProgress && status === DataStatuses.EXPORTED && (
              <Badge
                className={classNames(Css.badge, !reconciled && Css.active)}
                value={reconciled
                  ? (quickBooksBusiness ? uiTexts.matched : uiTexts.reconciled)
                  : (quickBooksBusiness ? uiTexts.awaitingMatching : uiTexts.awaitingReconciliation)} />
            )}
            {(status !== DataStatuses.TO_RECONCILE) && !inProgress && (
              <Button
                outline
                className={Css.viewDetails}
                icon={Icons.Eye}
                onClick={handleViewCategorizationDetailsClick}>
                {uiTexts.viewDetails}
              </Button>
            )}
          </>
        )}
      </div>
      <div className={Css.fields}>
        <div className={Css.contact}>
          <SelectContact
            disabled={typeTransfer || disableInputs || seeTheDocument}
            auditRoute={auditRoute}
            className={Css.input}
            vendorId={vendorId}
            address={address}
            currency={currency}
            transaction={transaction}
            transactionState={transactionState}
            placeholder={seeTheDocument ? uiTexts.seeTheDocument : undefined}
            onChange={handleContactChange}
            updateTransaction={updateTransaction} />
        </div>
        <div className={Css.dynamicBlock} data-width={`f${additionalInputsCount}`}>
          {rpaMode && needReactionStatus && !manualMode
            ? (
              <div className={Css.reason}>
                <ReasonInput
                  disabled={disableInputs}
                  value={reason}
                  audit={auditRoute}
                  localReasons={localReasons}
                  placeholder={messages.describeTransactionForAi}
                  onChange={handleReasonChange}
                  onBlur={onReasonBlur} />
              </div>
            ) : (
              <>
                {usesItems
                  ? (
                    <Item
                      disabled={disabledCategoryInput}
                      audit={!!auditRoute}
                      className={Css.input}
                      transaction={transaction}
                      item={item}
                      advancedType={advancedType}
                      validateInput={!disableInputs && (!needReactionStatus || manualMode || item.name)}
                      onChange={handleItemChange} />
                  )
                  : (
                    <SelectCategory
                      type={type}
                      audit={!!auditRoute}
                      className={Css.input}
                      disabled={disabledCategoryInput}
                      showVendorValidation={showVendorValidation}
                      transaction={transaction}
                      category={seeTheDocument ? {
                        displayName: type === TransactionTypes.WITHDRAW
                          ? AccountNames.ACCOUNT_PAYABLE
                          : AccountNames.ACCOUNT_RECEIVABLE
                      } : category}
                      vendorId={vendorId}
                      advancedType={advancedType}
                      validateCategory={!disableInputs}
                      onChange={handleCategoryChange} />
                  )}
                {!simplifyLayout && !usCountry && !!taxRates.length && (
                  <div className={Css.input}>
                    <SelectTaxRate
                      disabled={disabledExtraInput}
                      audit={!!auditRoute}
                      vendorId={vendorId}
                      showVendorValidation={showVendorValidation}
                      value={!seeTheDocument && taxRate}
                      placeholder={seeTheDocument ? uiTexts.seeTheDocument : undefined}
                      onChange={handleTaxRateChange} />
                  </div>
                )}
                {!simplifyLayout && !!classes.length && (
                  <div className={Css.input}>
                    <SelectClass
                      disabled={disabledExtraInput}
                      audit={!!auditRoute}
                      vendorId={vendorId}
                      showVendorValidation={showVendorValidation}
                      value={!seeTheDocument && classValue}
                      placeholder={seeTheDocument ? uiTexts.seeTheDocument : undefined}
                      onChange={handleClassChange} />
                  </div>
                )}
                {!simplifyLayout && !!locations.length && (
                  <div className={Css.input}>
                    <SelectLocation
                      disabled={disabledExtraInput}
                      audit={!!auditRoute}
                      vendorId={vendorId}
                      showVendorValidation={showVendorValidation}
                      value={!seeTheDocument && location}
                      placeholder={seeTheDocument ? uiTexts.seeTheDocument : undefined}
                      onChange={handleLocationChange} />
                  </div>
                )}
                {!simplifyLayout && !!projects.length && (
                  <div className={Css.input}>
                    <SelectProject
                      disabled={disabledExtraInput}
                      audit={!!auditRoute}
                      vendorId={vendorId}
                      showVendorValidation={showVendorValidation}
                      value={!seeTheDocument && project}
                      placeholder={seeTheDocument ? uiTexts.seeTheDocument : undefined}
                      onChange={handleProjectChange} />
                  </div>
                )}
              </>
            )}
        </div>
      </div>
      <div className={Css.footer}>
        {!inProgress && (
          <>
            {!simplifyLayout && (!auditRoute || quickBooksBusiness) && (
              <Attachment
                className={Css.attachment}
                disabled={(status === DataStatuses.EXPORTED && !quickBooksBusiness)
                  || (documentId && (status === DataStatuses.EXPORTED || status === DataStatuses.TO_REPORT))}
                transactionId={transactionId}
                documentId={documentId}
                attachmentId={attachmentId}
                attachmentName={attachmentName}
                attachmentKey={attachmentKey} />
            )}
            <Comments
              disabled={disabled}
              audit={!!auditRoute}
              status={status}
              transactionId={transactionId}
              comments={comments}
              askClientInfo={askClientInfo}
              onAskClient={handleAskClient} />
            <AdditionalInfo
              disabled={disabled || aiProcessing}
              disabledCategorySelect={disabledCategoryInput}
              auditRoute={auditRoute}
              usesItems={usesItems}
              status={status}
              type={type}
              vendorId={vendorId}
              documentId={documentId}
              category={category}
              initialReason={initialReason}
              initialCategory={initialCategory}
              onCategoryChange={handleCategoryChange}
              onCategorizedByClick={handleViewCategorizationDetailsClick} />
          </>
        )}
        {!inProgress && (
          <Actions
            disabled={disabled}
            auditRoute={auditRoute}
            selected={selected}
            selectable={selectable}
            readyToProcess={readyToProcess}
            readyToApprove={readyToApprove}
            transaction={transaction}
            transactionState={transactionState}
            onManualModeSet={handleManualModeSet}
            refetchTableData={refetchTableData}
            onSelectedChange={onSelectedChange}
            onDocumentEdit={handleDocumentEdit} />
        )}
      </div>
    </div>
  );
};

export default React.memo(ExtraCell);
